import { getAppEnvironment } from "../Enums/LegacyRedirection";

const PROFILE_PICTURE_CDN_ORIGIN =
  "https://cdn.worldservicesgroup.com/cdn-cgi/image";

export type ProfilePictureUrlOptions = {
  width?: number;
  height?: number;
  fit?: "cover" | "contain";
  gravity?: string;
  zoom?: number;
  cacheBust?: boolean | number;
  /** Cosmos `_ts` — fallback cache bust when `photo` has no timestamp. */
  photoUpdatedAt?: number | string;
};

function isProdStorageBackend(): boolean {
  const apiRoot = (process.env.REACT_APP_ROOT_API ?? "").toLowerCase();
  const blogStorage = (process.env.REACT_APP_BLOGSTORAGE_URL ?? "").toLowerCase();
  return (
    apiRoot.includes("api.serverless.worldservicesgroup.com") ||
    apiRoot.includes("wsgfilestorageqa") ||
    apiRoot.includes("wsgfilestorageprod") ||
    blogStorage.includes("wsgfilestorageqa") ||
    blogStorage.includes("wsgfilestorageprod")
  );
}

/** Azure storage account that receives profile picture uploads for the current API. */
function getProfilePictureBlobStorageAccount(): string {
  const explicit =
    process.env.REACT_APP_STORAGE_ACCOUNT_NAME ||
    process.env.REACT_APP_STORAGE_ACCOUNT;
  if (explicit?.trim()) {
    return explicit.trim();
  }

  const apiRoot = (process.env.REACT_APP_ROOT_API ?? "").toLowerCase();
  if (apiRoot.includes("wsgserverlessrelease")) {
    return "wsgfilestoragerelease";
  }
  if (apiRoot.includes("api.serverless.worldservicesgroup.com")) {
    return "wsgfilestorageprod";
  }
  return "wsgfilestorageqa";
}

function getProfilePictureBlobContainer(): string {
  const explicit =
    process.env.REACT_APP_STORAGE_ACCOUNT_CONTAINER_NAME ||
    process.env.REACT_APP_STORAGE_CONTAINER;
  if (explicit?.trim()) {
    return explicit.trim();
  }
  return getStorageEnvSegment();
}

/** CDN origin is wired to prod storage only; other accounts need direct blob URLs. */
function shouldUseProfilePictureCdn(): boolean {
  return getProfilePictureBlobStorageAccount().toLowerCase() === "wsgfilestorageprod";
}

function getProfilePictureDirectBlobUrl(
  personId: string,
  cacheBust?: number,
): string {
  const account = getProfilePictureBlobStorageAccount();
  const container = getProfilePictureBlobContainer();
  const base = `https://${account}.blob.core.windows.net/${container}/user/profile-pictures/${personId}/photo.jpg`;
  if (cacheBust !== undefined) {
    return `${base}?${cacheBust}`;
  }
  return base;
}

/** CDN/blob env prefix (prod, dev, uat, testing) — must match Azure container + path layout. */
export function getStorageEnvSegment(): string {
  const explicit = process.env.REACT_APP_ENVIRONMENT;
  if (explicit === "prod" || explicit === "release") {
    return "prod";
  }
  if (explicit === "uat") {
    return "uat";
  }
  if (explicit === "testing") {
    return "testing";
  }
  if (explicit === "dev") {
    return "dev";
  }

  const appEnv = getAppEnvironment();
  if (appEnv === "release") {
    return "prod";
  }
  if (appEnv === "prod" || appEnv === "uat" || appEnv === "testing") {
    return appEnv;
  }

  // localhost often resolves to dev but QA/prod API storage uses prod/ prefix
  if (isProdStorageBackend()) {
    return "prod";
  }

  return "dev";
}

/** Blob path for profile pictures (CDN source; not stored on `person.photo`). */
export function getProfilePictureStoragePath(personId: string): string {
  const id = personId?.trim();
  if (!id) {
    return "";
  }
  const env = getStorageEnvSegment();
  return `/${env}/user/profile-pictures/${id}/photo.jpg`;
}

const PROFILE_PHOTO_TIMESTAMP_RE = /^\d{10,17}$/;

/** `person.photo` is a millisecond timestamp used for CDN cache bust + legacy sync. */
export function parseProfilePhotoTimestamp(
  photo?: string | null,
): number | undefined {
  const raw = String(photo ?? "").trim();
  if (!PROFILE_PHOTO_TIMESTAMP_RE.test(raw)) {
    return undefined;
  }
  const n = Number(raw);
  return Number.isFinite(n) ? n : undefined;
}

export function createProfilePhotoTimestamp(): string {
  return String(Date.now());
}

const PROFILE_PHOTO_VERSION_STORAGE_KEY = (personId: string) =>
  `wsg:profilePhotoVersion:${personId}`;

export function getProfilePhotoCacheBust(
  personId: string,
): number | undefined {
  const id = personId?.trim();
  if (!id) {
    return undefined;
  }
  try {
    const raw = localStorage.getItem(PROFILE_PHOTO_VERSION_STORAGE_KEY(id));
    if (!raw) {
      return undefined;
    }
    const n = Number(raw);
    return Number.isFinite(n) ? n : undefined;
  } catch {
    return undefined;
  }
}

export function setProfilePhotoCacheBust(
  personId: string,
  value: number = Date.now(),
): void {
  const id = personId?.trim();
  if (!id) {
    return;
  }
  try {
    localStorage.setItem(PROFILE_PHOTO_VERSION_STORAGE_KEY(id), String(value));
  } catch {
    /* ignore quota / private mode */
  }
}

/** Cloudflare-transformed profile photo URL (face gravity handled server-side). */
export function getProfilePictureCdnUrl(
  personId: string,
  options: ProfilePictureUrlOptions = {},
): string {
  const id = personId?.trim();
  if (!id) {
    return "";
  }

  const {
    width = 240,
    height = 240,
    fit = "cover",
    gravity = "face",
    zoom = 0.4,
    cacheBust,
  } = options;

  const transforms = `w=${width},h=${height},fit=${fit},gravity=${gravity},zoom=${zoom}`;
  const env = getStorageEnvSegment();
  const base = `${PROFILE_PICTURE_CDN_ORIGIN}/${transforms}/${env}/user/profile-pictures/${id}/photo.jpg`;

  if (cacheBust) {
    const ts = typeof cacheBust === "number" ? cacheBust : Date.now();
    return `${base}?${ts}`;
  }
  return base;
}

function normalizePhotoVersionTimestamp(value: number): number {
  return value < 1e12 ? value * 1000 : value;
}

function resolveProfilePhotoCacheBust(
  personId: string,
  photo?: string | null,
  options: ProfilePictureUrlOptions = {},
): number | undefined {
  if (options.cacheBust !== undefined) {
    return typeof options.cacheBust === "number"
      ? options.cacheBust
      : Date.now();
  }

  // Server timestamp (`person.photo`) before localStorage so reload picks up new uploads.
  const fromPhoto = parseProfilePhotoTimestamp(photo);
  if (fromPhoto !== undefined) {
    return fromPhoto;
  }

  const stored = getProfilePhotoCacheBust(personId);
  if (stored !== undefined) {
    return stored;
  }

  if (options.photoUpdatedAt != null && options.photoUpdatedAt !== "") {
    if (typeof options.photoUpdatedAt === "number") {
      return normalizePhotoVersionTimestamp(options.photoUpdatedAt);
    }
    const parsed = Date.parse(options.photoUpdatedAt);
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }

  return undefined;
}

/**
 * Speaker/guest photos may be a full blob URL (guest catalog) or a profile timestamp (members).
 * Prefer explicit URLs; otherwise resolve via the profile-picture CDN/blob layout.
 */
export function resolveSpeakerPhotoDisplayUrl(
  personId: string | undefined,
  photo?: string | null,
  options: ProfilePictureUrlOptions = {},
): string {
  const raw = photo?.trim() ?? "";
  if (/^https?:\/\//i.test(raw)) {
    return raw;
  }
  return resolveProfilePictureDisplayUrl(personId, photo, options);
}

/**
 * Build CDN display URL from `personId`. `photo` is only read as a cache-bust timestamp,
 * never as an image path or URL.
 */
export function resolveProfilePictureDisplayUrl(
  personId: string | undefined,
  photo?: string | null,
  options: ProfilePictureUrlOptions = {},
): string {
  const id = personId?.trim();
  if (!id) {
    return "";
  }

  const cacheBust = resolveProfilePhotoCacheBust(id, photo, options);
  if (!shouldUseProfilePictureCdn()) {
    return getProfilePictureDirectBlobUrl(id, cacheBust);
  }
  return getProfilePictureCdnUrl(id, {
    ...options,
    ...(cacheBust !== undefined ? { cacheBust } : {}),
  });
}
